'use strict';

/**
 * NACRE Wave 3.2.1 — Browser bundle entry
 *
 * Bundled by esbuild → public/auth/vault-bundle.js → loaded by the
 * vault HTML pages. Exposes every browser-side function under a single
 * namespace `window.OysterVault` so HTML pages can use them without
 * any module system.
 *
 * The bundle includes:
 *   - signup-client (BIP39 generation, key derivation, wrap construction)
 *   - login-client (browser-side identity vault unwrap)
 *   - recovery-client (mnemonic-based unwrap + re-wrap)
 *   - module-vault-client (encrypt + ship + fetch + decrypt)
 *   - kdf, symmetric, seed, hybrid-kex, hybrid-sign, algo-versions (via dep chain)
 *
 * The bundle does NOT include any server-side code (vault-routes,
 * vault-registration, vault-login, etc.). Those run only on the server.
 *
 * Note on Buffer: the underlying crypto modules use Buffer. esbuild
 * polyfills it via the `buffer` npm package shim included in the build
 * script's externals/inject config.
 */

const signupClient = require('./signup-client');
const loginClient = require('./login-client');
// T576 (LD-1b) — client-side reload re-unlock orchestrator (T510 precedent).
const { reunlockSession } = require('./session-reunlock');
const recoveryClient = require('./recovery-client');
// T901 D5 step 3 — the CURRENT-PASSWORD variant of the shipped password
// re-wrap. NOT a `deriveMasterKey`: `BLK-T901-P7-client-half-…-20260809`
// UNBLOCK-PATH (a) is STRUCK because the SEAMS browser twin derives a key that
// decrypts nothing, and a password change needs no master-key KDF at all —
// `identityMaster` is invariant across it. Shares ONE re-wrap implementation
// with `recoveryClient.buildPasswordReset` (shared/crypto/password-rewrap.js).
const { buildPasswordChange } = require('./password-rewrap');
const moduleVaultClient = require('./module-vault-client');
const pinClient = require('./pin-client');
const pinUiClient = require('./pin-ui-client');
// T315 Module Lock — client-side warm primitive + pure policy/icon helpers
const { ensureModuleMasters } = require('../module-lock/ensure-master');
const moduleLockPolicy = require('../module-lock/policy');
const moduleLockIcon = require('../module-lock/golden-icon');
const moduleLockPassphrase = require('../module-lock/passphrase');
const moduleLockRemoveProof = require('../module-lock/remove-proof');
// T1123 — the Module Lock CLIENT half. Delivered through THIS bundle because
// every consumer (shared/cores/*/client/*.js, public/auth/*.html, the Control
// executors) is served RAW and cannot `require()`; a window global is the only
// seam they share.
const { createBlobLockFetch } = require('../module-lock/blob-lock-client');
// GOTCHA-575 (a) — fail-closed mint guard: never replace an existing master.
const { assessMint, MINT_VERDICTS } = require('../module-lock/mint-guard');
// Wave 25: FAST_TEST_PROFILE intentionally NOT imported here. It is a
// test-only KDF profile (low iteration count) and exposing it on
// window.OysterVault would let any caller silently downgrade KDF
// strength on real user passwords. Tests import it directly from
// './kdf' instead.
const { KDF_PROFILES } = require('./kdf');
const { generateMnemonic, validateMnemonic } = require('./seed');
// T2469F P1 (FIX 24) — the English BIP39 list the per-word recovery boxes hint
// against. Exposed from the ONE copy `seed.js` already validates with (the plan's
// anti-fix: never ship a second wordlist). Public data; exposing it widens nothing.
const { wordlist: bip39Wordlist } = require('@scure/bip39/wordlists/english.js');
const hybridKex = require('./hybrid-kex');
const symmetric = require('./symmetric');
// T372 — sealed-box per-record seal/unseal (server-blind chat history). The
// CLIENT unseals history records on read with its vault-resident private kex
// key. Composes hybrid-kex + @noble/ciphers gcmsiv (pure-JS, browser-safe).
const sealBox = require('./seal-box');
// T372 integrity follow-on — the client verifies the seq+prevHash chain on
// read (reorder/middle-drop/dup), checks the recipient-kex self-signature, and
// computes the tail-truncation high-water-mark.
const sealBoxIntegrity = require('./seal-box-integrity');
// T550 Private Vault — the CLIENT-side seal composition (meta+dek cbox-v1 +
// AES-256-GCM body). The browser seals under its OWN public kex and PUTs a
// pre-sealed record; the strongbox route is server-blind (refuses plaintext).
const strongboxSeal = require('../vaults/strongbox/store-sealbox');
// T-cert sealed SAVE — media-editor doc/asset seal agents (the T538
// USER-ACTION). Master-key seal from the live session (_identityMaster);
// keyless ⇒ typed ERR_MASTER_KEY_REQUIRED throw (never a plaintext PUT).
const { createMediaEditorSealAgent } = require('./media-editor-seal');
// B2/B4 pre-cutover — JIT decrypt-grant mint. STATELESS key-provider seam:
// key material is caller-supplied per call. Keyless ⇒ typed
// ERR_JIT_KEY_MATERIAL_REQUIRED throw.
const { mintJitGrant, encodeJitGrantToken } = require('./jit-grant-mint');
// B2/B4 custody DECISION (B4 LD-8 option (b), browser topology): the signing
// capability is a NON-EXTRACTABLE WebCrypto Ed25519 signer derived from the
// master key at unlock, held on the in-memory session as session.jitSigner
// (page lifetime, never storage). Cross-page it travels ONLY as the derived
// sign-only seed inside the T529 one-time E2E handoff (deriveJitSigningSeed),
// imported + zeroized on arrival. jitFetch is the ONE shared wrapper that
// mints + attaches `Authorization: JIT` when — and only when — the server
// demands it (inert pass-through while JIT_GRANT_ENFORCEMENT is OFF).
const { createJitSigner, deriveJitSigningSeed } = require('./jit-signer');
const { createJitFetch } = require('./jit-fetch');
// T451 P3.1 — tasks pilot, the BROWSER half of browser-only decrypt: opens the
// at-rest task-store envelope (served VERBATIM by the sealed /api/task-lists
// handler when TASKS_BROWSER_DECRYPT=1) under the live session's master key and
// normalizes it with the SAME shared helpers index.js delegates to. Mirrors the
// media-editor seal agent below (same session resolver, same keyless contract).
const { createTaskStoreUnsealAgent } = require('../cores/tasks/task-store-unseal');
// T451 P3.3 — mail CONTENT (comms message store): open a 🔴 message body /
// thread preview the server handed through verbatim under COMMS_BROWSER_DECRYPT.
const { createCommsMessageUnsealAgent } = require('../cores/comms/comms-message-unseal');
const { createCrewV1ThreadUnsealAgent } = require('../cores/comms/comms-crew-v1-unseal');

// ── Cross-page session persistence (Wave 6.3e) ──
// The full vault session (privateKeys + moduleMasters + _identityMaster)
// lives in window memory for the page lifetime ONLY and is NEVER written to
// sessionStorage. Across multi-page navigation we persist only NON-SECRET
// operational metadata (accountNumber / publicKeys / policies) so the
// "logged-in" UI state survives a page load. Key material does not survive
// navigation — a new page re-unlocks (fresh password) to recover the keys.
//
// SECURITY (T213 / H19-t2): sessionStorage is readable by ANY same-origin
// script, so a single XSS on `/auth/*` would exfiltrate anything stored here.
// The earlier "no LESS secure than the in-memory window object" reasoning was
// wrong: `window.OysterVault.session` is a closure-reachable object an
// attacker must already have code-execution to reach AND that dies with the
// page, whereas sessionStorage is a durable, enumerable string store. We
// therefore persist via a strict ALLOWLIST (see persistSessionToStorage).

const SESSION_STORAGE_KEY = 'oyster-vault-session-v1';

function _u8ToHex(arr) {
  let hex = '';
  for (let i = 0; i < arr.length; i++) hex += arr[i].toString(16).padStart(2, '0');
  return hex;
}
function _hexToU8(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  return bytes;
}
function _serialize(value) {
  if (value instanceof Uint8Array) return { __u8: _u8ToHex(value) };
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const out = {};
    for (const k of Object.keys(value)) out[k] = _serialize(value[k]);
    return out;
  }
  if (Array.isArray(value)) return value.map(_serialize);
  return value;
}
function _deserialize(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (typeof value.__u8 === 'string') return _hexToU8(value.__u8);
    const out = {};
    for (const k of Object.keys(value)) out[k] = _deserialize(value[k]);
    return out;
  }
  if (Array.isArray(value)) return value.map(_deserialize);
  return value;
}

// SECURITY (T213 / H19-t2): the ONLY session fields that may be written to
// sessionStorage. Strict ALLOWLIST — a blacklist (`const { _identityMaster,
// ...rest } = session`) silently re-leaks every NEW secret field a future
// session shape adds, which is exactly how this regressed: the source kept
// the blacklist while only the build artifact was patched, so the next bundle
// rebuild reintroduced the leak. Every field here is NON-SECRET operational
// metadata. Unwrapped key material — privateKeys, moduleMasters,
// _identityMaster, _accountPassword — must NEVER appear.
const SESSION_PERSIST_ALLOWLIST = ['accountNumber', 'publicKeys', 'policies'];

function persistSessionToStorage(session) {
  if (typeof sessionStorage === 'undefined') return;
  if (!session || typeof session !== 'object') return;
  // Copy ONLY allowlisted non-secret fields into a fresh object (never mutate
  // the caller's in-memory session, which keeps its keys for the page life).
  const safe = {};
  for (const k of SESSION_PERSIST_ALLOWLIST) {
    if (session[k] !== undefined) safe[k] = session[k];
  }
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(_serialize(safe)));
  } catch (e) {}
}

function restoreSessionFromStorage() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return _deserialize(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

function clearSessionFromStorage() {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch (e) {}
}

// ── T546 gate-a — media-editor live-collab key agent ──────────────────────────
// The collab connector (shared/media-editor/collab/collab-connect.js) consumes an
// injected keyAgent for the room-DEK exchange. This bridges its four methods from
// the LIVE vault session (privateKeys.{kex,sign}, unwrapped at login, browser-only)
// and the published-keys route. Feature-detected + FAIL-CLOSED: absent session or
// keys ⇒ null ⇒ the connector never calls session.join ⇒ the editor stays single-
// user (same posture as the vault save seal). The room DEK + the KEX/SIGN private
// keys live in BROWSER memory only; the server relays opaque wraps and never
// decapsulates (CLAUDE.md Legal Foundation). No plaintext / SESSION_SECRET collab.
function _meWindow() { return (typeof window !== 'undefined') ? window : null; }

// Resolve the warm vault session from: the LOCAL page (OyOS module view), else the
// same-origin HOST page across the editor iframe boundary (window.parent/top) — the
// OysterVaultKex-bridge / private-vault-panel precedent. Do NOT force a re-login in
// the iframe; a cross-origin host is simply unreachable ⇒ single-user (try/catch).
function _meResolveSession() {
  const w = _meWindow();
  if (!w) return null;
  try {
    if (w.OysterVault && w.OysterVault.session) return w.OysterVault.session;
    const host = (w.parent && w.parent !== w) ? w.parent : ((w.top && w.top !== w) ? w.top : null);
    if (host && host.OysterVault && host.OysterVault.session) return host.OysterVault.session;
  } catch (_e) { /* cross-origin host ⇒ no bridge ⇒ single-user */ }
  return null;
}

function _meFetchPublicKeys(account) {
  const w = _meWindow();
  const f = (typeof fetch !== 'undefined') ? fetch : (w && w.fetch);
  if (typeof f !== 'function' || account == null) return Promise.resolve(null);
  return f('/api/users/' + encodeURIComponent(String(account)) + '/publicKeys', { credentials: 'same-origin' })
    .then((r) => (r && r.ok) ? r.json() : null)
    .then((j) => (j && j.ok && j.publicKeys) ? j.publicKeys : null)
    .catch(() => null);
}

const mediaEditorCollabKeys = {
  // JOINER: recipient hybrid KEX private key (unwraps the host-wrapped room DEK).
  getOwnKexPrivate() {
    const w = _meWindow();
    if (w && w.OysterVaultKex && typeof w.OysterVaultKex.getPrivateKex === 'function') {
      try { const k = w.OysterVaultKex.getPrivateKex(); if (k) return k; } catch (_e) { /* fall through */ }
    }
    const s = _meResolveSession();
    return (s && s.privateKeys && s.privateKeys.kex) || null;
  },
  // HOST: creator hybrid SIGN private key (signs the wrapped-DEK distribution).
  getOwnSignPrivate() {
    const w = _meWindow();
    if (w && w.OysterVaultKex && typeof w.OysterVaultKex.getPrivateSign === 'function') {
      try { const k = w.OysterVaultKex.getPrivateSign(); if (k) return k; } catch (_e) { /* fall through */ }
    }
    const s = _meResolveSession();
    return (s && s.privateKeys && s.privateKeys.sign) || null;
  },
  // HOST: the recipient's published { kex, sign } (stored form). kexSig is forwarded
  // ONLY if the route returns one — it never does today (ND-1 open, GOTCHA-291), so
  // the connector's verifyRecipientKex refuses and co-edit stays dark by design.
  getMemberKeys(account) {
    return _meFetchPublicKeys(account).then((pk) => {
      if (!pk || !pk.kex || !pk.sign) return null;
      const out = { kex: pk.kex, sign: pk.sign };
      if (pk.kexSig != null) out.kexSig = pk.kexSig; // forward-compat pass-through only
      return out;
    });
  },
  // JOINER: the seam creator's published SIGN public (stored form) to verify the wrap.
  getCreatorSignPublic(account) {
    return _meFetchPublicKeys(account).then((pk) => (pk && pk.sign) || null);
  },
};

// T-cert sealed SAVE — the media-editor seal agent instance, bound to the SAME
// session resolver the collab key agent uses (local page → same-origin host).
// The master key never leaves this closure; editor-boot only ever receives
// envelopes (or a typed keyless throw).
const mediaEditorSeal = createMediaEditorSealAgent({ resolveSession: _meResolveSession });

// T451 P3.1 — the tasks unseal agent, bound to the SAME session resolver. The
// master key never leaves this closure; the tasks client receives typed
// { status, state } results (ok / absent / locked / sealed-under-a-different-key…).
const taskStoreUnseal = createTaskStoreUnsealAgent({ resolveSession: _meResolveSession });

// T451 P3.3 — the comms message unseal agent, bound to the SAME session
// resolver. The master key never leaves this closure; the messages clients
// receive typed { status, text } results per row (ok / plain / cbox / locked /
// record-binding-mismatch / sealed-under-a-different-key…).
const commsMessageUnseal = createCommsMessageUnsealAgent({ resolveSession: _meResolveSession });
const crewV1Unseal = createCrewV1ThreadUnsealAgent({ resolveSession: _meResolveSession });

// Construct the public API
const OysterVault = {
  // Signup
  buildSignupPayload: signupClient.buildSignupPayload,

  // T207 client-side auth proof — the SINGLE verifier every auth page submits
  // instead of the raw password (login.html, signup.html, recovery.html all
  // call window.OysterVault.deriveAuthProof). signup-client re-exports the
  // canonical shared/crypto/auth-proof.js implementation. Without this line the
  // consolidated login throws "deriveAuthProof is not a function".
  deriveAuthProof: signupClient.deriveAuthProof,

  // Login
  unlockIdentityVault: loginClient.unlockIdentityVault,

  // T576 (LD-1b) — reload re-unlock: re-derive the vault session CLIENT-SIDE
  // via the existing auth-proof path (challenge → proof → wrapped-vault →
  // unlockIdentityVault). No server key custody; dispatches `vault-ready`.
  reunlockSession: (args) => reunlockSession(Object.assign({ vault: OysterVault }, args)),

  // Recovery
  unlockWithMnemonic: recoveryClient.unlockWithMnemonic,
  buildPasswordReset: recoveryClient.buildPasswordReset,

  // T901 D5 step 3 — authenticated "change my password". The page unwraps the
  // vault with the CURRENT password, re-wraps the SAME identityMaster under the
  // new one, and POSTs {currentPasswordProof, newSaltPw, newWrapPw,
  // newPasswordProof} to /auth/vault/me/password. The server sees neither
  // password nor the unwrapped identityMaster.
  buildPasswordChange,

  // Module data
  saveModuleData: moduleVaultClient.saveModuleData,
  loadModuleData: moduleVaultClient.loadModuleData,
  createSaveLoadAdapter: moduleVaultClient.createSaveLoadAdapter,

  // Per-module PIN protection (Wave 6.2)
  derivePinKey: pinClient.derivePinKey,
  wrapModuleMasterWithPin: pinClient.wrapModuleMasterWithPin,
  unwrapModuleMasterWithPin: pinClient.unwrapModuleMasterWithPin,
  validatePinFormat: pinClient.validatePinFormat,
  buildPinWrapEnvelope: pinClient.buildPinWrapEnvelope,
  unwrapPinEnvelope: pinClient.unwrapPinEnvelope,

  // Vault-lock UI state machine (Wave 6.3a; de-PIN'd names T643 LD-2).
  // Both names are exported: key-overlay.js prefers the vault-lock name, so a
  // page served from a bundle built before T643 still resolves the legacy one.
  createVaultLockSession: pinUiClient.createVaultLockSession,
  VAULT_LOCK_STATES: pinUiClient.VAULT_LOCK_STATES,
  createPinUiSession: pinUiClient.createPinUiSession,
  PIN_STATES: pinUiClient.STATES,
  DEFAULT_AUTO_LOCK_MS: pinUiClient.DEFAULT_AUTO_LOCK_MS,

  // ── Module Lock (T315) ──
  // Client-side warm primitive (LD-9): mints any missing module masters into
  // session, re-seals vault.bin under the in-memory _identityMaster, and PUTs
  // the ciphertext. Fixes "module master not in session" without the password.
  ensureModuleMasters,
  // GOTCHA-575 (a): decides whether minting a module master is SAFE, from
  // durable server state (key envelope / data blob) rather than the
  // sessionStorage that may have just cleared. Refuses — and asks for
  // re-derivation — for any account that already has a master, and for any
  // inconclusive probe. Minting stays legal only for proven first use.
  assessMint,
  MINT_VERDICTS,
  ModuleLock: {
    createDefaultPolicy: moduleLockPolicy.createDefaultPolicy,
    migratePolicy: moduleLockPolicy.migratePolicy,
    validatePolicy: moduleLockPolicy.validatePolicy,
    setFactor: moduleLockPolicy.setFactor,
    requiredFactors: moduleLockPolicy.requiredFactors,
    isLockedForAction: moduleLockPolicy.isLockedForAction,
    hasAnyLock: moduleLockPolicy.hasAnyLock,
    listFactorTypes: moduleLockPolicy.listFactorTypes,
    ML_STRINGS: moduleLockPolicy.ML_STRINGS,
    iconState: moduleLockIcon.iconState,
    shacklePath: moduleLockIcon.shacklePath,
    estimateStrength: moduleLockPassphrase.estimateStrength,
    checkAgainstAccountPassword: moduleLockPassphrase.checkAgainstAccountPassword,
    isAllowed: moduleLockPassphrase.isAllowed,
    computeRemoveProof: moduleLockRemoveProof.computeRemoveProof,
  },

  // Cross-page session persistence (Wave 6.3e)
  persistSessionToStorage,
  restoreSessionFromStorage,
  clearSessionFromStorage,

  // T546 gate-a — media-editor live-collab key agent (own KEX/SIGN private +
  // published-keys resolver). Feature-detected + fail-closed (see definition
  // above); absent session/keys ⇒ the editor stays single-user.
  mediaEditorCollabKeys,

  // T-cert sealed SAVE — media-editor doc/asset master-key seal agents (the
  // T538 USER-ACTION editor-boot feature-detects). Session resolves LOCAL page
  // first, then the same-origin HOST across the iframe boundary (the T546
  // _meResolveSession bridge). Keyless ⇒ typed ERR_MASTER_KEY_REQUIRED throw.
  sealMediaEditorDoc: mediaEditorSeal.sealMediaEditorDoc,
  unsealMediaEditorDoc: mediaEditorSeal.unsealMediaEditorDoc,
  sealMediaEditorAsset: mediaEditorSeal.sealMediaEditorAsset,
  unsealMediaEditorAsset: mediaEditorSeal.unsealMediaEditorAsset,

  // T1075 P1 — the Audio Editor account project doc (locked recordType
  // 'media-editor-audioproj'; packs reuse the asset pair above). The DAW
  // panel (P4) feature-detects this pair the same way editor-boot does.
  sealAudioProjectDoc: mediaEditorSeal.sealAudioProjectDoc,
  unsealAudioProjectDoc: mediaEditorSeal.unsealAudioProjectDoc,

  // T678 P3 — the website-editor site-doc pair. The control bundle's website
  // bridge feature-detects these exactly the way editor-boot feature-detects
  // the media pair, and refuses (typed) when they are absent: an editor that
  // cannot seal must not fall back to writing anything.
  sealWebsiteSiteDoc: mediaEditorSeal.sealWebsiteSiteDoc,
  unsealWebsiteSiteDoc: mediaEditorSeal.unsealWebsiteSiteDoc,

  // T451 P3.1 — tasks pilot (browser-only decrypt): opens the sealed
  // /api/task-lists envelope under the live session's master key. Typed
  // three-state result; never throws; the key never leaves the agent closure.
  unsealTaskStore: taskStoreUnseal.unsealTaskStore,

  // T451 P3.4 — the tasks WRITE path: seal the (mutated) whole store back
  // under the session master key, shaped like the server sink (narrowed +
  // stamped + hybrid PQ). The mutations are the SAME module index.js runs, so
  // the browser applies exactly what the server would have; the render
  // normalizer is the one the server used.
  sealTaskStore: taskStoreUnseal.sealTaskStore,
  taskMutations: require('../cores/tasks/task-mutations'),
  normalizeTaskStateForUser: require('../cores/tasks/task-state').normalizeTaskStateForUser,

  // T451 P3.4b — the sibling views (routines-view.js, code-workspace-view.js)
  // write through ONE shared sealed writer (unseal → mutate with the shared
  // logic → seal → PUT verbatim) instead of a third and fourth copy of the
  // P3.4 machinery. The Washdown routine is instantiated in the page from the
  // same pure params builder the server's POST /api/tasks/routine uses, and
  // the completion ding is decided by the same helper the increment route uses.
  createSealedTaskWriter: require('../cores/tasks/task-sealed-writer').createSealedTaskWriter,
  routineParamsFromTemplate: require('../cores/tasks/washdown-routine-params').routineParamsFromTemplate,
  routineCompletionState: require('../cores/tasks/tasks-ding-emitter').routineCompletionState,

  // T451 P3.3 — mail CONTENT (comms message store, browser-only decrypt): opens
  // a verbatim 🔴 body / preview row under the live session's master key,
  // binding the envelope to the row it was sealed for. Typed; never throws.
  unsealCommsText: commsMessageUnseal.unsealCommsText,
  unsealCommsRows: commsMessageUnseal.unsealCommsRows,

  // T451 P3.3b — the in-app crew store's legacy v1 threads: open the chain
  // master key → messenger private key → thread content key → body in the
  // page, from the ciphertext-only shape comms-api.js serves under
  // COMMS_BROWSER_DECRYPT. Typed; never throws; binding checked before the key.
  unsealCrewV1Thread: crewV1Unseal.unsealCrewV1Thread,

  // B2/B4 pre-cutover — JIT decrypt-grant mint + header-token encoder (the
  // `Authorization: JIT <token>` form auth/require-jit-grant.js decodes).
  // Key material is a per-call parameter — never stored, never cached here.
  mintJitGrant,
  encodeJitGrantToken,

  // B2/B4 custody — the sign-only session signer (non-extractable WebCrypto
  // where available) + the handoff-seed derivation + the shared JIT fetch
  // wrapper factory. See the require-site comment for the custody doctrine.
  createJitSigner,
  deriveJitSigningSeed,
  createJitFetch,

  // T529 LD-4 — the __u8 typed-array codec, exported for the one-time login
  // handoff: login.html _serialize()s the secret bundle BEFORE wrapping it
  // under the one-time key K; module-client _deserialize()s after unwrap
  // (a plain JSON round-trip corrupts Uint8Arrays). NOT a storage surface —
  // the T213 SESSION_PERSIST_ALLOWLIST above is untouched.
  _serialize,
  _deserialize,

  // Helpers
  generateMnemonic,
  validateMnemonic,
  // T2469F P1 — read-only view for the recovery page's per-box word hints.
  bip39Wordlist: Object.freeze(bip39Wordlist.slice()),

  // Profiles (caller should pick the right one for their device).
  // Wave 25: FAST_TEST_PROFILE deliberately omitted — it would
  // weaken KDF for real users if anything in the bundle reached for it.
  KDF_PROFILES,

  // Hybrid key exchange (VoIP E2E encryption — Wave 26.33)
  encapsulate: hybridKex.encapsulate,
  decapsulate: hybridKex.decapsulate,
  generateKeyPair: hybridKex.generateKeyPair,
  // T372 sealed-box (server-blind chat history) — the client unseals each
  // history record on read with session.privateKeys.kex. seal() is here for
  // symmetry / client-side re-seal; the server seals at its store chokepoint.
  sealRecord: sealBox.sealRecord,
  unsealRecord: sealBox.unsealRecord,
  SEAL_MARKER: sealBox.SEAL_MARKER,
  // T550 Private Vault — client-side vault seal/unseal (also on window.OysterSealBox).
  sealVaultItem: strongboxSeal.sealItem,
  unsealVaultItem: strongboxSeal.unsealItem,
  sealVaultMetaField: strongboxSeal.sealMetaField,
  unsealVaultMetaField: strongboxSeal.unsealMetaField,
  // T372 integrity follow-on (client-side chain verification + HWM).
  verifyHistoryChain: sealBoxIntegrity.verifyChain,
  verifyHistoryChainAgainstHwm: sealBoxIntegrity.verifyChainAgainstHwm,
  computeHistoryHwm: sealBoxIntegrity.computeHwm,
  signRecipientKex: sealBoxIntegrity.signRecipientKex,
  verifyRecipientKex: sealBoxIntegrity.verifyRecipientKex,
  // Symmetric encryption (AES-256-GCM)
  symmetricEncrypt: symmetric.encrypt,
  symmetricDecrypt: symmetric.decrypt,

  // Build metadata
  version: 'v1',
  builtAt: new Date().toISOString(),
};

// B2/B4 — the DEFAULT shared jitFetch instance every gated call site resolves
// (`window.OysterVault.jitFetch`). Signer + identity are read at CALL time from
// the live session, so the instance is safe to construct before login and
// degrades to a pure pass-through whenever either is absent.
if (typeof fetch === 'function') {
  OysterVault.jitFetch = createJitFetch({
    resolveSigner: () => (OysterVault.session && OysterVault.session.jitSigner) || null,
    resolveUserId: () => {
      const s = OysterVault.session;
      if (s && s.accountNumber != null && s.accountNumber !== '') return String(s.accountNumber);
      const meta = restoreSessionFromStorage();
      if (meta && meta.accountNumber != null && meta.accountNumber !== '') return String(meta.accountNumber);
      try {
        if (typeof localStorage !== 'undefined') {
          const v = localStorage.getItem('oyster-active-account-id');
          if (v) return v;
        }
      } catch (_e) { /* storage unavailable → no identity → pass-through */ }
      return null;
    },
  });
}

// T1123 — the Module Lock client half, on the SAME transport jitFetch sits on.
//
// Every dependency is resolved at CALL time, never at construction: this file
// runs once at bundle load, `OysterVault.jitFetch` is assigned just above,
// `OysterVault.session` arrives at login, and `window.OysterVaultLock` is a
// SEPARATE deferred delivery (key-overlay.js). A construction-time lookup would
// pin whatever happened to exist at parse time for the whole page lifetime —
// the same trap module-client.js and the Control executors each name in place.
//
// CHALLENGE-RESPONSE: with FEATURE_MODULE_LOCK off the server never emits a
// `module-lock` refusal, so this is a pure pass-through and flag-OFF traffic is
// byte-identical.
if (typeof fetch === 'function') {
  OysterVault.lockedBlobFetch = createBlobLockFetch({
    baseFetch: (url, opts) => (typeof OysterVault.jitFetch === 'function'
      ? OysterVault.jitFetch(url, opts)
      : fetch(url, opts)),
    resolveMaster: (moduleId) => {
      const s = OysterVault.session;
      return (s && s.moduleMasters && s.moduleMasters[moduleId]) || null;
    },
    // The step-up prompt, when the page delivers one. Absent ⇒ the server's own
    // refusal is relayed unchanged, which is the stricter pole.
    requireUnlock: async (moduleId) => {
      const lock = (typeof window !== 'undefined' && window.OysterVaultLock) || null;
      if (lock && typeof lock.requireUnlock === 'function') await lock.requireUnlock(moduleId);
    },
  });
}

// T550 Private Vault — dedicated seal-box namespace the vault panel consumes.
// `seal` is the full-item seal (meta+dek cbox-v1 + AES-GCM body); `unsealRecord`
// is the single-record unseal the panel already used to open item metadata.
const OysterSealBox = {
  seal: strongboxSeal.sealItem,
  sealItem: strongboxSeal.sealItem,
  unsealItem: strongboxSeal.unsealItem,
  sealMetaField: strongboxSeal.sealMetaField,
  unsealMetaField: strongboxSeal.unsealMetaField,
  sealRecord: sealBox.sealRecord,
  unsealRecord: sealBox.unsealRecord,
};

// Expose to window when loaded in a browser. Also export for the
// build-script test path.
if (typeof window !== 'undefined') {
  window.OysterVault = OysterVault;
  window.OysterSealBox = OysterSealBox;
}

module.exports = { OysterVault, OysterSealBox };
